import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Box, TableContainer, TableHead, TableRow, TableCell, TableBody, Table, Paper, useTheme, Pagination, TextField, Typography, Skeleton, IconButton } from '@mui/material';
import { blue, grey } from '@mui/material/colors';

import { Add } from '@mui/icons-material';
import RowTableCliente from '../SubComponents/Clientes/RowTableCliente';
import DialogCreateCliente from '../SubComponents/Clientes/DialogCreateCliente';
import EmptyState from '../common/EmptyState';
import { getTopSearchMatchIds } from '../../utils/searchHighlights';

import { useAuth } from '../../Auth/AuthContext';
const API_URL = process.env.REACT_APP_API_URL;


function ClientesPage({ handleChangePage }) {
  const { user } = useAuth();
  const areUserAdmin = user.rol === 'Administrador';

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [clientes, setClientes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const rowsPerPage = 30; // request page_size for backend

  const [openDialogCreate, setOpenDialogCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const deleteRequestsRef = useRef(new Set());

  const fetchClientes = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    const params = {
      page: currentPage,
      page_size: rowsPerPage,
    };
    if (searchTerm) {
      params.search = searchTerm;
      // if the search term looks like comma-separated numbers, also send as ids filter
      if (/^\s*\d+(?:\s*,\s*\d+)*\s*$/.test(searchTerm)) {
        params.ids = searchTerm;
      }
    }

    axios
      .get(`${API_URL}/clientes/`, { params })
      .then((response) => {
        setClientes(response.data.results || []);
        const total = response.data.count || 0;
        setCount(total);
        setTotalPages(Math.ceil(total / rowsPerPage));
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching clientes:', error);
        setLoadError(true);
        setLoading(false);
      });
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const handleDeleteCliente = useCallback(async (id) => {
    if (deleteRequestsRef.current.has(id)) return;
    deleteRequestsRef.current.add(id);
    try {
      await axios.delete(`${API_URL}/clientes/${id}/`);
      setClientes((current) => current.filter((cliente) => cliente.id !== id));
    } catch (error) {
      console.error('Error deleting cliente:', error);
    } finally {
      deleteRequestsRef.current.delete(id);
    }
  }, []);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleUpdateData = useCallback((updatedCliente) => {
    setClientes((prevData) =>
      prevData.map((cliente) =>
        cliente.id === updatedCliente.id ? updatedCliente : cliente
      )
    );
  }, []);

  const searchHighlightIds = useMemo(() => new Set(getTopSearchMatchIds(
    clientes,
    searchTerm,
    ['id', 'nombre', 'apellidos', 'documento', 'celular']
  )), [clientes, searchTerm]);

  // prepare rows with memo to avoid unnecessary re-renders
  const rows = useMemo(
    () =>
      clientes.map((item) => (
        <RowTableCliente
          key={item.id}
          data={item}
          handleDelete={handleDeleteCliente}
          handleUpdateData={handleUpdateData}
          areUserAdmin={areUserAdmin}
          searchHighlighted={searchHighlightIds.has(item.id)}
        />
      )),
    [areUserAdmin, clientes, handleDeleteCliente, handleUpdateData, searchHighlightIds]
  );

  return (
    <Box sx={{ paddingTop: 1, position: 'relative', height: '100%' }}>
      <Typography variant='h5' color='grey'>TOTAL DE CLIENTES: {count}</Typography>
      <TextField 
        label="Buscar Cliente Por Cualquier Dato"
        variant="outlined"
        sx={{ paddingBottom: 2, paddingTop: 0 }}
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={handleSearchChange}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{
            backgroundColor: isDarkMode ? grey[800] : blue[700],
            color: isDarkMode ? 'black' : 'white',
          }}>
            <TableRow>
            <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>ID</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>NOMBRE</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>APELLIDOS</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>TIPO DOCUMENTO</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>DOCUMENTO</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', borderRight: areUserAdmin ? null : '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>CELULAR</TableCell>
              { areUserAdmin && <TableCell align="center"sx={{borderLeft: '2px solid #ddd',borderBottom: '2px solid #ddd',borderRight: '2px solid #ddd',fontWeight: 'bold',color: 'white',verticalAlign: 'middle'}}>
                ACCIONES
                  <IconButton onClick={() => {setOpenDialogCreate(true)}}>
                     <Add sx={{ color: 'white' }}/>
                  </IconButton>
                </TableCell> }
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(rowsPerPage)].map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton variant="text" width={40} /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  {areUserAdmin && <TableCell><Skeleton variant="rectangular" width={100} height={40} /></TableCell>}
                </TableRow>
              ))
            ) : (
              clientes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={areUserAdmin ? 7 : 6}>
                    <EmptyState message={loadError ? 'No fue posible cargar los clientes.' : searchTerm ? 'No se encontraron resultados.' : 'No hay clientes para mostrar.'} />
                  </TableCell>
                </TableRow>
              ) : rows
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
        <Pagination 
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
      <DialogCreateCliente
        openDialog={openDialogCreate}
        handleCloseDialog={() => {setOpenDialogCreate(false)}}
        handleUpdateData={fetchClientes}
      />
    </Box>
  );
}

export default ClientesPage;
